import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { IJob } from '~/domain/Jobs/types/jobsTypes';
import { useBulkConfirmJobs } from '~/domain/Jobs/useCases/useBulkConfirmJobs';
import { useUserSelector } from '~/hooks/useUserSelector';
import { WorkerClassificationTypeEnum } from '~/models';

interface IUseBulkConfirmActions {
  candidateId: string;
}

export const useBulkConfirmation = ({ candidateId }: IUseBulkConfirmActions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notes_for_temp } = useUserSelector();
  const { workerClassification } = useUserSelector();

  const isEmployee =
    workerClassification?.classificationType === WorkerClassificationTypeEnum.Employee;

  const bulkConfirmMutation = useBulkConfirmJobs();

  const confirmJobs = useCallback(
    (jobs: IJob[], selectedJobIds: string[]) => {
      if (selectedJobIds.length === 0 || jobs.length === 0) {
        throw new Error('No jobs selected for confirmation');
      }

      bulkConfirmMutation.mutate({
        jobs,
        selectedJobIds,
        candidateId,
        notes: notes_for_temp || '',
      });
    },
    [candidateId, notes_for_temp, bulkConfirmMutation],
  );

  const handleCloseSuccessPopup = useCallback(() => {
    if (!isEmployee) {
      navigate('/');
    }
    bulkConfirmMutation.reset();
    queryClient.removeQueries({ queryKey: ['jobs-infinite'] });
  }, [isEmployee, navigate, queryClient, bulkConfirmMutation]);

  const handleCloseErrorPopup = useCallback(() => {
    bulkConfirmMutation.reset();
    queryClient.removeQueries({ queryKey: ['jobs-infinite'] });
  }, [bulkConfirmMutation, queryClient]);

  return {
    confirmJobs,
    isLoadingJobsConfirmation: bulkConfirmMutation.isPending,
    isJobsConfirmationSuccess: bulkConfirmMutation.isSuccess && !bulkConfirmMutation.isPending,
    bulkConfirmationError: bulkConfirmMutation.error,
    handleCloseErrorPopup,
    handleCloseSuccessPopup,
    confirmedJobIds: bulkConfirmMutation.data || [],
  };
};
